import { tokens } from '@sparkpost/design-tokens';
import { Lock, LockOpen } from '@sparkpost/matchbox-icons';
import { formatDate } from '@sparkpost/report-builder/helpers/date';
import React from 'react';
import { ExternalLink, SubduedLink } from 'src/components/links';
import {
  Banner,
  Box,
  Column,
  Columns,
  LabelValue,
  Layout,
  Panel,
  Stack
} from 'src/components/matchbox';
import { SubduedText } from 'src/components/text';
import { ERROR_CODE_CONFIG, EXTERNAL_LINKS } from '../constants';
import TlsStatusBadge from './TlsStatusBadge';

function isRenewalDateInFuture(tlsCertificate) {
  if (!tlsCertificate?.expires_at) {
    return false;
  }
  return new Date() < new Date(tlsCertificate.expires_at);
}

export default function TrackingDomainTLSCertificateSection({ domain, isSectionVisible }) {
  if (!isSectionVisible) {
    return null;
  }

  const {
    uses_managed_certificate: usesManagedCertificate,
    status,
    tls_certificate: tlsCertificate,
    verified,
    secure
  } = domain;

  const tlsStatusValid = status?.tls_status === 'valid';
  const showRenewalDate = isRenewalDateInFuture(tlsCertificate);
  const errorCode = tlsCertificate?.error_code;
  const errorConfig = errorCode ? ERROR_CODE_CONFIG[errorCode] || ERROR_CODE_CONFIG.unknown : null;

  const isManagedTls = usesManagedCertificate;
  const isSelfManagedHttps = !usesManagedCertificate && secure;
  const isHttpOnly = !usesManagedCertificate && !secure;

  const managedTlsLockColor =
    status?.tls_status === 'valid' ? tokens.color_green_500 : tokens.color_gray_500;

  return (
    <Layout>
      <Layout.Section annotated>
        <Layout.SectionTitle as="h2">HTTPS</Layout.SectionTitle>
        <Stack>
          <SubduedText fontSize="200">
            Configure HTTPS for secure click and open tracking.
          </SubduedText>
          <SubduedLink
            as={ExternalLink}
            to={EXTERNAL_LINKS.TRACKING_DOMAIN_HTTPS_DOCUMENTATION}
            fontSize="200"
          >
            HTTPS Documentation
          </SubduedLink>
        </Stack>
      </Layout.Section>
      <Layout.Section>
        <Panel>
          {isHttpOnly && (
            <Panel.Section>
              <Box display="flex" alignItems="flex-start" gap="300">
                <LockOpen size={35} color={tokens.color_yellow_500} />
                <Box ml={18}>
                  <strong>HTTPS Disabled</strong>
                  <SubduedText fontSize="200" marginTop="100">
                    HTTP-only domains can be flagged by web browsers and negatively impact
                    engagement rates.
                  </SubduedText>
                </Box>
              </Box>
            </Panel.Section>
          )}

          {isSelfManagedHttps && (
            <Panel.Section>
              <Box display="flex" alignItems="flex-start" gap="300">
                <Lock size={35} color={tokens.color_gray_500} />
                <Box ml={18}>
                  <strong>HTTPS Enabled</strong> via self-managed infrastructure
                  <SubduedText fontSize="200" marginTop="100">
                    Your domain is configured to use HTTPS through your own CDN or proxy server.
                    Make sure your CDN is properly configured to forward traffic to spgo.io.
                  </SubduedText>
                </Box>
              </Box>
            </Panel.Section>
          )}

          {isManagedTls && (
            <>
              <Panel.Section>
                <Box display="flex" alignItems="flex-start" gap="300">
                  <Lock size={35} color={managedTlsLockColor} />
                  <Box ml={20}>
                    <strong>HTTPS Enabled</strong> via managed TLS certificate
                    <SubduedText fontSize="200" marginTop="100">
                      SparkPost automatically manages a TLS certificate for this tracking domain,
                      enabling secure connections for click and open tracking.
                    </SubduedText>
                  </Box>
                </Box>
                {errorConfig && (
                  <Banner status="danger" size="small" marginTop="400">
                    {errorConfig.description}
                  </Banner>
                )}
                {!verified && (
                  <Banner status="warning" size="small" marginTop="400">
                    Verify your domain to issue to get a valid certificate.
                  </Banner>
                )}
              </Panel.Section>
              <Panel.Section>
                <Columns>
                  <Column>
                    <LabelValue>
                      <LabelValue.Label>Certificate Status</LabelValue.Label>
                      <LabelValue.Value>
                        <TlsStatusBadge tlsStatus={status?.tls_status} verified={verified} />
                      </LabelValue.Value>
                    </LabelValue>
                  </Column>
                  {tlsStatusValid && tlsCertificate?.issued_at && (
                    <Column>
                      <LabelValue>
                        <LabelValue.Label>Issued At</LabelValue.Label>
                        <LabelValue.Value>
                          {formatDate.useMomentInput(tlsCertificate.issued_at)}
                        </LabelValue.Value>
                      </LabelValue>
                    </Column>
                  )}
                  {tlsStatusValid && showRenewalDate && (
                    <Column>
                      <LabelValue>
                        <LabelValue.Label>Next Renewal</LabelValue.Label>
                        <LabelValue.Value>
                          {formatDate.useMomentInput(tlsCertificate.expires_at)}
                        </LabelValue.Value>
                      </LabelValue>
                    </Column>
                  )}
                </Columns>
              </Panel.Section>
            </>
          )}
        </Panel>
      </Layout.Section>
    </Layout>
  );
}
